import * as React from "react";
import { Button, Checkbox } from "@fluentui/react-components";
import {
  speEditingService,
  type WorkspaceStatus,
  type OfficeEvent,
} from "../../services/speEditingService";
import {
  DocumentWriteError,
  documentWriteMessage,
} from "../../services/documentWriteError";
import { useAuth } from "../../context/AuthContext";
// Die Namen folgen den Ereignissen, die das Backend tatsächlich schreibt.
const eventNames: Record<string, string> = {
  WORKSPACE_STARTED: "Bearbeitung in Office begonnen",
  WORKSPACE_JOINED: "Weiterer Bearbeiter dazugekommen",
  SYNC_IMPORTED: "In KMVS gesichert",
  "sync-backup-required": "Sicherung nicht gelungen, wird wiederholt",
  BUDGET_BLOCKED: "Sicherung wegen der Kostengrenze zurückgestellt",
  RIGHTS_REVOKED: "Schreibrecht eines Bearbeiters entzogen",
  CLOSE_CONFIRMED: "Sicherung bestätigt",
  CLOSE_CONFIRMED_BY_ADMIN: "Sicherung stellvertretend durch die Administration bestätigt",
  AUTO_CLOSE_IDLE: "Seit Tagen unverändert – von KMVS automatisch geschlossen",
  CLOSE_CONFIRMED_BY_SYSTEM: "Sicherung von KMVS bestätigt: Datei bei Microsoft unverändert",
  CLOSE_BLOCKED: "Abschluss wartet noch",
  CONTAINER_PURGED: "Bearbeitung abgeschlossen, Arbeitskopie bei Microsoft gelöscht",
  PURGE_DEFERRED: "Bearbeitung abgeschlossen; schreibgeschützte Kopie bei Microsoft wird später gelöscht",
  PURGE_RETRY_FAILED: "Löschen der Kopie bei Microsoft erneut gescheitert, wird wiederholt",
  PROVISION_ABORTED: "Vorbereitung abgebrochen",
  WORKSPACE_IDLE_REMINDER: "Arbeitskopie seit über einem Tag offen",
  WORKSPACE_ESCALATION: "Arbeitskopie seit über einer Woche offen",
};
/** Nur diese Gründe bedeuten, dass der Stand in KMVS noch nicht vollständig ist. */
const backupIncomplete = new Set([
  "spe_final_readback_failed",
  "spe_final_local_hash_mismatch",
  "spe_versions_incomplete",
  "spe_readback_too_large",
  "spe_remote_missing_without_proof",
]);
export const OfficeWorkspaceStatus: React.FC<{
  documentId: number;
  initialStatus: WorkspaceStatus;
  onStatus?: (status: WorkspaceStatus) => void;
}> = ({ documentId, initialStatus, onStatus }) => {
  const [status, setStatus] = React.useState(initialStatus);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // Eigener Zustand: ein Aussetzer beim Abfragen verschwindet mit der nächsten Antwort.
  const [pollFailed, setPollFailed] = React.useState(false);
  const [askOnBehalf, setAskOnBehalf] = React.useState(false);
  const { isAdmin } = useAuth();
  const [agreedTag, setAgreedTag] = React.useState<string | null>(null);
  const [askClose, setAskClose] = React.useState(false);
  const [events, setEvents] = React.useState<OfficeEvent[] | null>(null);
  const running = React.useRef(false);
  const callback = React.useRef(onStatus);
  callback.current = onStatus;
  const apply = React.useCallback((next: WorkspaceStatus) => {
    setStatus(next);
    callback.current?.(next);
  }, []);
  React.useEffect(() => {
    setStatus(initialStatus);
  }, [initialStatus]);
  React.useEffect(
    () => setAgreedTag(null),
    [
      status.workspaceId,
      status.confirmationTag,
      status.ownConfirmationPending,
      status.confirmationRequired,
    ],
  );
  React.useEffect(() => {
    if (status.state === "closed") return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout>;
    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(
        async () => {
          if (!alive) return;
          try {
            const next = await speEditingService.status(documentId);
            if (alive) setPollFailed(false);
            if (alive && next) {
              apply(next);
              if (next.state === "closed") return;
            }
          } catch {
            if (alive) setPollFailed(true);
          }
          if (alive) schedule();
        },
        document.visibilityState === "hidden" ? 60_000 : 10_000,
      );
    };
    schedule();
    document.addEventListener("visibilitychange", schedule);
    return () => {
      alive = false;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", schedule);
    };
  }, [documentId, status.state, apply]);
  const run = async (action: () => Promise<WorkspaceStatus>) => {
    if (running.current) return;
    running.current = true;
    setBusy(true);
    setError(null);
    try {
      apply(await action());
      setAskClose(false);
      setAskOnBehalf(false);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Die Aktion ist fehlgeschlagen. Die Arbeitskopie bleibt erhalten.",
      );
      if (
        e instanceof DocumentWriteError &&
        e.code === "spe_confirmation_version_changed"
      ) {
        setAgreedTag(null);
        try {
          const next = await speEditingService.status(documentId);
          if (next) apply(next);
        } catch {
          /* Fehler bleibt sichtbar */
        }
      }
    } finally {
      running.current = false;
      setBusy(false);
    }
  };
  const label =
    status.state === "closed"
      ? "Bearbeitung abgeschlossen"
      : status.state === "failed" ||
          (status.state === "cleanup_blocked" &&
            !!status.cleanupReason &&
            backupIncomplete.has(status.cleanupReason))
        ? "Sicherung fehlgeschlagen – Arbeitskopie bleibt erhalten"
        : status.state === "cleanup_blocked"
          ? "Abschluss wartet – Arbeitskopie bleibt erhalten"
        : status.state === "cleanup_pending"
          ? "Aufräumen ausstehend"
          : status.syncPending ||
              status.state === "sync_pending" ||
              status.state === "closing"
            ? "Änderungen werden übernommen"
            : status.state === "provisioning"
              ? "Office wird vorbereitet"
              : "In Office geöffnet";
  return (
    <section
      aria-label="Office-Sicherungsstatus"
      style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}
    >
      <strong role="status">{label}</strong>
      {status.lastSavedAt ? (
        <p>
          In KMVS gesichert um{" "}
          {new Date(status.lastSavedAt).toLocaleString("de-DE")}
        </p>
      ) : (
        <p>Noch kein in KMVS gesicherter Stand nachgewiesen.</p>
      )}
      <p>
        {status.participants} aktuelle Bearbeiter.{" "}
        {status.confirmedParticipants} bisherige Bearbeiter haben bestätigt.
      </p>
      {status.cleanupReason && (
        <p>
          {documentWriteMessage(
            status.cleanupReason,
            "Der Abschluss ist noch nicht möglich. Die Arbeitskopie bleibt erhalten.",
          )}
        </p>
      )}
      {error && <p role="alert">{error}</p>}
      {pollFailed && (
        <p role="alert">
          Der Sicherungsstatus konnte nicht aktualisiert werden. Die Arbeitskopie bleibt erhalten.
        </p>
      )}
      {status.state !== "closed" && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button
            disabled={busy}
            onClick={() => void run(() => speEditingService.sync(documentId))}
          >
            Jetzt sichern
          </Button>
          <Button
            disabled={busy || status.state === "provisioning"}
            onClick={() => setAskClose(true)}
          >
            Bearbeitung abschließen
          </Button>
        </div>
      )}
      {askClose && (
        <div>
          <p>
            Der gemeinsame Abschluss betrifft alle Bearbeiter. Speichern Sie
            alle Änderungen, auch offline, und schließen Sie die Office-Fenster.
            Fremde Office-Fenster werden nicht automatisch geschlossen.
          </p>
          <Button
            disabled={busy}
            onClick={() => void run(() => speEditingService.close(documentId))}
          >
            Gemeinsamen Abschluss anfordern
          </Button>
          <Button disabled={busy} onClick={() => setAskClose(false)}>
            Abbrechen
          </Button>
        </div>
      )}
      {status.confirmationRequired && (
        <div>
          <p>
            {status.missingConfirmations} Sicherungsbestätigungen fehlen noch.
            Die Arbeitskopie bleibt bis zum sicheren Abschluss erhalten.
          </p>
          {status.ownConfirmationPending ? (
            <>
              <Checkbox
                label="Alle meine Änderungen sind in Office gespeichert; auch offline gibt es keine ungespeicherten Änderungen. Meine Office-Fenster sind geschlossen."
                checked={
                  !!status.confirmationTag &&
                  agreedTag === status.confirmationTag
                }
                disabled={busy || !status.confirmationTag}
                onChange={(_, data) =>
                  setAgreedTag(
                    data.checked === true ? status.confirmationTag : null,
                  )
                }
              />
              <Button
                disabled={
                  busy ||
                  agreedTag !== status.confirmationTag ||
                  !status.confirmationTag
                }
                onClick={() => {
                  const seenTag = agreedTag;
                  if (seenTag && seenTag === status.confirmationTag)
                    void run(() =>
                      speEditingService.confirmSaved(documentId, seenTag),
                    );
                }}
              >
                Sicherung bestätigen
              </Button>
            </>
          ) : (
            <p>
              Für Sie ist aktuell keine zusätzliche Bestätigung angefordert. Bei einem neuen Stand kann erneut eine Bestätigung erforderlich werden.
            </p>
          )}
          {isAdmin && status.missingConfirmations > 0 && status.confirmationTag && (
            askOnBehalf ? (
              <div>
                <p>
                  Damit bestätigen Sie, dass die fehlenden Bearbeiter nichts
                  Ungespeichertes mehr in Office haben. Ungespeicherte
                  Änderungen dieser Bearbeiter gehen mit dem Abschluss verloren.
                </p>
                <Button
                  disabled={busy}
                  onClick={() => {
                    const seenTag = status.confirmationTag;
                    if (seenTag)
                      void run(() =>
                        speEditingService.confirmSavedOnBehalf(documentId, seenTag),
                      );
                  }}
                >
                  Stellvertretend bestätigen
                </Button>
                <Button disabled={busy} onClick={() => setAskOnBehalf(false)}>
                  Abbrechen
                </Button>
              </div>
            ) : (
              <Button disabled={busy} onClick={() => setAskOnBehalf(true)}>
                Für fehlende Bearbeiter bestätigen
              </Button>
            )
          )}
        </div>
      )}
      <Button
        disabled={busy}
        onClick={() => {
          setBusy(true);
          setError(null);
          void speEditingService
            .events(documentId)
            .then(setEvents)
            .catch(() =>
              setError("Die Aktivitäten konnten nicht geladen werden."),
            )
            .finally(() => setBusy(false));
        }}
      >
        Aktivitäten
      </Button>
      {events && (
        <ul>
          {events.map((e) => (
            <li key={e.id}>
              {new Date(e.createdAt).toLocaleString("de-DE")} ·{" "}
              {e.actorKind === "system" ? "System" : "Bearbeiter"}:{" "}
              {eventNames[e.action] ?? "Office-Aktivität"}
              {e.result === "error" ? " – fehlgeschlagen" : ""}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
